import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Shield, Heart, Clock } from "lucide-react";
import logo from "@/assets/kinship-logo.webp";
import EmailLink from "@/components/EmailLink";
import NewsletterSignup from "@/components/NewsletterSignup";

const Footer = () => {
  return (
    <footer className="bg-foreground text-primary-foreground">
      {/* Trust badges bar */}
      <div className="border-b border-primary-foreground/10">
        <div className="container py-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-primary-foreground/70">
              <Shield className="h-4 w-4 text-primary-foreground/75" />
              <span>Background-Checked Caregivers</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-primary-foreground/70">
              <Heart className="h-4 w-4 text-primary-foreground/75" />
              <span>Locally Owned in Nashville</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-primary-foreground/70">
              <Clock className="h-4 w-4 text-primary-foreground/75" />
              <span>Free Consultations</span>
            </div>
          </div>
          <p className="text-center text-sm text-primary-foreground/70 leading-relaxed mt-5 max-w-3xl mx-auto">
            Every family deserves clear answers to the questions above — including about us. If you're curious where Kinship Care Collective stands on licensing, insurance, or how we structure our care team, ask us directly; we'd rather have that conversation than leave it to guesswork.
          </p>
        </div>
      </div>

      <div className="container py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src={logo} alt="Kinship Care Collective — senior and elder care in Nashville" className="h-14 w-14" loading="lazy" width={512} height={512} />
              <div>
                <h2 className="font-heading text-xl font-semibold leading-tight">Kinship Care Collective</h2>
                <p className="text-[0.7rem] uppercase tracking-[0.18em] text-primary-foreground/80">
                  In-Home Companion Care for Older Adults
                </p>
              </div>
            </div>
            <p className="text-primary-foreground/70 text-sm leading-relaxed mb-4">
              Trusted senior and elder care serving Nashville, Brentwood, Franklin, and Belle Meade, TN. We support older adults aging at home — we are not a children's or foster kinship care service.
            </p>
            <p className="text-primary-foreground/75 text-xs italic">
              We take care of your family like our own.
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-primary-foreground/80">Quick Links</h3>
            <nav aria-label="Footer navigation" className="flex flex-col gap-2">
              {[
                { to: "/services", label: "Services" },
                { to: "/senior-care-nashville", label: "Senior Care in Nashville" },
                { to: "/home-care-nashville", label: "Home Care in Nashville" },
                { to: "/elder-care-nashville", label: "Elder Care in Nashville" },
                { to: "/respite-care-nashville", label: "Respite Care in Nashville" },
              { to: "/for-seniors", label: "For Older Adults" },
                { to: "/for-families", label: "For Families" },
                { to: "/for-caregivers", label: "For Caregivers" },
                { to: "/about", label: "About" },
                { to: "/resources", label: "Resources" },
                { to: "/tools/caregiver-burnout-check", label: "Free Caregiver Burnout Check" },
                { to: "/shop", label: "The Kinship Shop" },
                { to: "/shop/book", label: "The Survival Guide (Book)" },

                { to: "/consultation", label: "Free Consultation" },
                { to: "/care-request", label: "Request Care" },
                { to: "/resources/local-help", label: "Nashville Local Help" },
                { to: "/refer", label: "Refer a Family ($100)" },
                { to: "/partners", label: "For Referral Partners" },
                { to: "/guides/5-questions", label: "5 Questions Before Hiring Care" },
                { to: "/share-your-experience", label: "Share Your Experience" },
                { to: "/review", label: "Leave Us a Review" },
                { to: "/contact", label: "Contact" },
              ].map((link) => (
                <Link key={link.to} to={link.to} className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors">
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-primary-foreground/80">Services</h3>
            <div className="flex flex-col gap-2 text-sm text-primary-foreground/70">
              <span>Daily Companionship</span>
              <span>Transportation</span>
              <span>Medication Reminders</span>
              <span>Caregiver Placement</span>
              <span>Overnight Care</span>
              <span>Memory Care Activities</span>
              <span>Cook-Together Companionship</span>
              <span>Post-Hospital Support</span>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-primary-foreground/80">Contact</h3>
            <div className="flex flex-col gap-3 text-sm text-primary-foreground/70">
              <a href="tel:+16158070618" className="flex items-center gap-2 hover:text-primary-foreground transition-colors">
                <Phone className="h-4 w-4 shrink-0" />
                <span>(615) 807-0618</span>
              </a>
              <EmailLink className="flex min-w-0 items-center gap-2 hover:text-primary-foreground transition-colors">
                <Mail className="h-4 w-4 shrink-0" />
                <span className="break-all">hello<span aria-hidden="true">&#64;</span>kinshipcarecollective.com</span>
              </EmailLink>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0" />
                <span>Serving Nashville, Brentwood, Franklin, and Belle Meade, TN</span>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="font-semibold mb-2 text-xs uppercase tracking-wider text-primary-foreground/80">Service Areas</h3>
              <p className="text-xs text-primary-foreground/75 leading-relaxed">
                Nashville, Brentwood, Franklin, and Belle Meade, TN — plus surrounding Middle Tennessee communities including Bellevue, Mt. Juliet, Murfreesboro, Nolensville, Spring Hill, Smyrna, and La Vergne.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-primary-foreground/10 mt-10 pt-8">
          <div className="max-w-2xl">
            <h3 className="font-heading text-lg mb-2">Caregiver resources in your inbox</h3>
            <p className="text-sm text-primary-foreground/70 mb-4">
              Practical guides for families caring for an older adult in Nashville — home safety, planning
              ahead, and local support. A few emails a year, never more.
            </p>
            <NewsletterSignup source="footer" variant="footer" />
          </div>
        </div>

        <div className="border-t border-primary-foreground/10 mt-10 pt-6 flex flex-col gap-4 text-sm text-primary-foreground/75">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p>© {new Date().getFullYear()} Kinship Care Collective. All rights reserved.</p>
            <p className="text-xs">Non-medical home care only — we do not provide medical, nursing, or clinical services.</p>
          </div>
          <p className="text-xs leading-relaxed text-primary-foreground/60">
            All digital goods (books, kits, planners, and downloadable materials) are non-refundable. Our books, kits,
            planners, guides, and other published materials are provided for general informational and organizational
            purposes only. They are not medical, legal, financial, or professional caregiving advice, and purchasing or
            reading them does not create a caregiver, medical, legal, or advisory relationship with Kinship Care
            Collective. Always consult a qualified professional before making medical, legal, financial, or
            care-related decisions.
          </p>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
